import { ElementRef, Renderer2, ViewChild, Output, EventEmitter, ChangeDetectorRef, Directive } from '@angular/core';
import { NgForm } from '@angular/forms';
import { select } from '@angular-redux/store';
import { ItemData } from 'sharedFeatures/form';
import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { catchError, take, filter, takeUntil } from 'rxjs/operators';
import { XeroService } from 'sharedFeatures/sharedServices';
import { SaveClosePane } from "sharedFeatures/save-close-pane";
import { AuthData } from "sharedFeatures/sharedModels";
import { AuthUtils, Utils } from "sharedFeatures/utils";
import { KeyValue } from "@angular/common";
import { XeroLinkDescriptor } from "sharedFeatures/sharedModels/xero/xeroLinkDescriptor";
import {AssociationsModel} from "sharedFeatures/sharedModels/xero/associationsModel";
import {AccountsModel} from "sharedFeatures/sharedModels/xero/accountsModel";
import {LinkHeadingFiltersListModel} from "sharedFeatures/sharedModels/xero/linkHeadingFiltersListModel";
import {AssociationsSubModel} from "sharedFeatures/sharedModels/xero/associationsSubModel";
import {BaseComponent} from "sharedFeatures/base";
import { DomSmallUtils } from 'sharedGuiSmall/utils/dom-utils';
import { LoadingSpinnerService } from 'sharedGuiSmall/sharedServices/loadingSpinner.service';

@Directive()
export class Xi4DataAssociationsBase extends BaseComponent {

    ca: AssociationsModel = new AssociationsModel();

    @ViewChild(NgForm)
    private form!: NgForm;

    public xeroAccounts: AccountsModel = {};
    public xeroLinkHeadingFiltersList: LinkHeadingFiltersListModel = new LinkHeadingFiltersListModel();

    accountsMap: Map<string, string> = new Map<string, string>();

    public isShowClose: boolean = true;

    comboItems: Array<ItemData> = [
        { id: 0, value: 'Last years data', disabled: false },
        { id: 1, value: 'Repeat the last month', disabled: false },
        { id: 2, value: 'Blank - enter manually', disabled: false }
    ];

    columnTitlesMap: Map<string, Array<string>> = new Map([
        ['Income components', ['Revenue (P&L)']],
        ['Cost of Sales components', ['Cost of sales (P&L)']],
        ['Operational Cost components', ['Other operating expenses (P&L)', 'Prepayment & Accrual Assets (Balance Sheet)']],
        ['Employee components', ['Salaries (P&L)', 'Pensions & Benefits (P&L)', 'Employers national insurance (P&L)', 'Wages payable (BS)', 'Pensions & benefits payable (BS)', 'National insurance payable (BS)']],
        ['Asset components', ['Fixed asset account (BS)', 'Accumulated depreciation (BS)', 'Depreciation (P&L)', 'Profit or loss on disposal of asset (P&L)']],
        ['Investment components', ['Investment (BS)', 'Investment Gain / Loss (P&L))' ]],
        ['Inventory components', ['Inventory expense (P&L)', 'Inventory value (BS)']],
        ['Loan components', ['Loan Balance (BS)', 'Interest charged (P&L)']],
        ['Equity components', ['Dividend (P&L)', 'Share Capital (BS)']],
        ['Capital components', ['Capital (BS)']],
        ['Subscription components', ['Revenue (P&L)', 'Prepayment & Accrual Liabilities (BS)']]
    ]
    );

    objectKeys = Object.keys;

    protected saveClosePane: SaveClosePane | undefined;

    leftTitleMaxWidth: number = 0;

    protected static workMode: boolean = false;

    @Output() onCOASaved = new EventEmitter<boolean>();

    protected afterSaveCallback: Function | undefined;

    readonly storeUnsubscribe = new Subject<void>();

    @select(['account', 'authData'])
    readonly authData$!: Observable<AuthData>;

    @select(['xeroSettings', 'bsAccounts'])
    readonly bsAccounts$!: Observable<AccountsModel>;
    bsAccountsData: AccountsModel = {};

    @select(['xeroSettings', 'plAccounts'])
    readonly plAccounts$!: Observable<AccountsModel>;
    plAccountsData: AccountsModel = {};

    isXeroEmployee: boolean = false;
    compType: string = '';

    protected chDet: ChangeDetectorRef;

    public initialCA: AssociationsModel = new AssociationsModel();

    constructor(
        el: ElementRef,
        r: Renderer2,
        protected xeroService: XeroService,
        chDet: ChangeDetectorRef,
        protected loadingSpinnerService: LoadingSpinnerService
    ) {
        super(el, r);
        this.chDet = chDet;

        this.bsAccounts$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                this.bsAccountsData = x;
            });

        this.plAccounts$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                this.plAccountsData = x;
            });
    }

    ngOnInit() {

        this.authData$.pipe(
            catchError(error => {
                console.error('Get AuthData error', error);
                return observableThrowError(error);
            }),
            filter(x => !!x),
            take(1))
            .subscribe((authData: AuthData) => {

                this.isXeroEmployee = AuthUtils.isBetaFeatureActive(authData, 'XeroEmployee');
            });

    }

    private getTitleWidth(title: string, cls: string): number {

        var text = document.createElement('div');
        this.r.setStyle(text, 'position', 'absolute');
        this.r.setStyle(text, 'visibility', 'invisible');
        document.body.appendChild(text);

        this.r.addClass(text, cls);

        this.r.appendChild(text, document.createTextNode(title));

        let w = DomSmallUtils.width(text) + 30;

        document.body.removeChild(text);

        return w;

    }

    private calculateLeftTitleMaxWidth(): void {

        let maxName = '';

        Object.keys(this.ca).forEach((key) => {

            const item = this.ca[key];

            Object.keys(item).forEach((k: string) => {

                const name = (!Xi4DataAssociationsBase.workMode) ? this.accountsMap.get(k) : this.extractComponentName(k);

                if (name && name.length > maxName.length) maxName = name;

            });

        });

        if ('Brixx component'.length > maxName.length) maxName = 'Brixx component';

        this.leftTitleMaxWidth = this.getTitleWidth(maxName, 'p1Font');

    }

    curtailAccount(title: string): string {

        if (title.length > 28) {
            title = title.substring(0, 28) + '...';
        }

        return title;
    }

    getHeaderWidth(title: string): number {
        return 370;
    }

    getTitleSizes(key: string): Array<number> {

        let arr: Array<number> = [];
        const titles = this.columnTitlesMap.get(key);

        if (titles)
            titles.forEach((title) => {

                arr.push(this.getHeaderWidth(title));

            });

        return arr;

    }

    getForm(): NgForm {
        return this.form;
    }

    public saveClosePanePlaceEl(): Element | undefined { return undefined; }

    public getPlanGuid(): string { return ''; }

    public getWorkMode(): boolean {

        return Xi4DataAssociationsBase.workMode;

    }

    public reload(callback: Function): void {

    }

    getIcon(item: KeyValue<string, AssociationsSubModel>) {
        return item.key.substring(0, item.key.length-11).toLowerCase().replace(/ /g, '-') + '-input-icon';
    }

    loadComponentAssociations(isExistingAssociations: boolean, isConnectedToXero: boolean, planGUID: string, callback?: Function) {

        const loadCallback = (componentAssociations: any) => {
            if (typeof (componentAssociations.success) == "undefined") return;

            if (componentAssociations.success)
            {
                this.ca = componentAssociations.ComponentAssociations;
                this.initialCA = structuredClone(componentAssociations.ComponentAssociations);
                this.xeroAccounts = componentAssociations.XeroAccounts;
                this.xeroLinkHeadingFiltersList = componentAssociations.xeroLinkHeadingFiltersList;

                this.fillAccountsMap();
                this.calculateLeftTitleMaxWidth();
                this.chDet.detectChanges();
                if (callback) callback(false);
            }
            else
            {
                
                this.loadingSpinnerService.setLoading(false);
                if (callback) callback(true);

            }
        };

        const plAccounts = this.plAccountsData;
        const bsAccounts = this.bsAccountsData;

        if (isExistingAssociations)
        {
            this.xeroService.XeroGetImportedComponentAssociations(isConnectedToXero, planGUID, plAccounts, bsAccounts).then(loadCallback);
        }
        else
        {
            this.xeroService.XeroGetComponentAssociations(plAccounts, bsAccounts).then(loadCallback);
        }
    }

    getPercentFieldDisplayValue(originalVal: number): string {
        return Utils.formatNumberByLangSettings(originalVal.toString()) + '%';
    }

    sortAccNull(a: KeyValue<string, AssociationsSubModel>, b: KeyValue<string, AssociationsSubModel>): number {
        // dummy sorting for keyvalue original order
        return 0;
    }

    sortAccSubNull(a: KeyValue<string, Array<XeroLinkDescriptor>>, b: KeyValue<string, Array<XeroLinkDescriptor>>): number {
        // dummy sorting for keyvalue original order
        return 0;
    }

    fillAccountsMap() {
        for (let key in this.xeroAccounts) {
            let curKey = this.xeroAccounts[key];
            if (curKey)
                curKey.forEach((account) => {
                    this.accountsMap.set(account.AccountCode, account.AccountName);
                });
        }
    }

    public isSaveClosePaneHidden(): boolean {

        return !this.saveClosePane || this.saveClosePane.isHidden();

    }

    onSave(autosave: boolean, callback?: Function): void {

        this.loadingSpinnerService.setLoading(true);
        let planGUID = this.getPlanGuid();

        this.xeroService.SetImportedComponentAssociations(this.ca, planGUID).then((data) => {

            if (typeof (data.success) == "undefined") return;

            this.loadingSpinnerService.setLoading(false);

            this.onCOASaved.emit();
            if (this.afterSaveCallback) this.afterSaveCallback(autosave);

        });

        if (this.saveClosePane) this.saveClosePane.close();
        this.saveClosePane = undefined;

    }

    public onGoOut(callback?: Function): void {

        if (callback) callback();

    }

    protected onParentClose(): void {

        if (this.saveClosePane) this.saveClosePane.close();
        this.saveClosePane = undefined;

    }

    extractComponentName(combinedName: string): string {
        return combinedName.split(";")[0];
    }

    onClose() {

    }

    getScrollTop(): number {

        let el = this.getElDom();

        return el.scrollTop;
    }

    setScrollTop(top: number): void {

        let el = this.getElDom();

        el.scrollTop = top;
    }

    onCancel(): void {

        const scrollPos = this.getScrollTop();
        this.reload(() => { this.setScrollTop(scrollPos); });

        if (this.saveClosePane) {
            this.saveClosePane.close();
            this.saveClosePane = undefined;
        }

    }

   onXeroLinkChange() {

   }

    activateLink(key: Array<XeroLinkDescriptor>) {

    }

    protected onGetOff(): void {

        if (this.saveClosePane && !this.isSaveClosePaneHidden()) {

            this.onSave(true, () => {

                if (this.afterSaveCallback) this.afterSaveCallback();

            });

            if (this.saveClosePane) this.saveClosePane.close();
            this.saveClosePane = undefined;

        }

    }

    ngOnDestroy() {

        this.onGetOff();
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }

}
